import React, { useEffect, useState } from 'react';
import Button from '../common/Button';
import GradientButton from '../common/GradientButton';
import Input from '../common/Input';
import Modal from '../common/Modal';
import { recordMatchResultApi } from '../../api/match.api';
import { getPlayersApi } from '../../api/player.api';
import { toJalaliFull } from '../../utils/jalali';


function buildInitialRows(match) {
  if (!match?.playerStatistics?.length) return [];
  return match.playerStatistics.map((stat) => ({
    key: `existing-${stat.id}`,
    playerId: String(stat.playerId),
    // باشگاهِ زمان بازی (نه لزوماً باشگاه فعلی بازیکن که ممکن است بعداً منتقل شده باشد)
    team: (stat.clubId ?? stat.player?.clubId) === match.homeClubId ? 'home' : 'away',
    goals: stat.goals || 0,
    assists: stat.assists || 0,
    yellowCards: stat.yellowCards || 0,
    redCards: stat.redCards || 0,
  }));
}

export default function MatchResultForm({ open, onClose, onSaved, match }) {
  const [homeScore, setHomeScore] = useState(match?.result?.homeScore ?? '');
  const [awayScore, setAwayScore] = useState(match?.result?.awayScore ?? '');
  const [report, setReport] = useState(match?.result?.report ?? '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open || !match) return;

    setHomeScore(match?.result?.homeScore ?? '');
    setAwayScore(match?.result?.awayScore ?? '');
    setReport(match?.result?.report ?? '');
    setRows(buildInitialRows(match));
    setError('');

    (async () => {
      try {
        const [homeRes, awayRes] = await Promise.all([
          getPlayersApi({ clubId: match.homeClubId, limit: 100 }),
          getPlayersApi({ clubId: match.awayClubId, limit: 100 }),
        ]);
        setHomePlayers(homeRes.data.data || []);
        setAwayPlayers(awayRes.data.data || []);
      } catch {
        setHomePlayers([]);
        setAwayPlayers([]);
      }
    })();
  }, [open, match]);

  if (!open || !match) return null;

  const addRow = (team) => {
    setRows((prev) => [
      ...prev,
      { key: `new-${Date.now()}-${Math.random()}`, team, playerId: '', goals: 0, assists: 0, yellowCards: 0, redCards: 0 },
    ]);
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (homeScore === '' || awayScore === '') {
      setError('امتیاز هر دو تیم الزامی است');
      return;
    }

    const invalidRow = rows.find((r) => !r.playerId);
    if (invalidRow) {
      setError('برای همه‌ ی سطرهای آمار، انتخاب بازیکن الزامی است');
      return;
    }

    setSubmitting(true);
    try {
      const playerStats = rows.map((r) => ({
        playerId: Number(r.playerId),
        goals: Number(r.goals) || 0,
        assists: Number(r.assists) || 0,
        yellowCards: Number(r.yellowCards) || 0,
        redCards: Number(r.redCards) || 0,
      }));

      await recordMatchResultApi(match.id, {
        homeScore: Number(homeScore),
        awayScore: Number(awayScore),
        report,
        playerStats,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'ثبت نتیجه ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTeamRows = (team, label, players) => {
    const teamRows = rows.filter((r) => r.team === team);

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h5 className="text-xs font-bold text-slate-600">{label}</h5>
          <button
            type="button"
            onClick={() => addRow(team)}
            className="rounded-lg bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-600 transition hover:bg-emerald-100"
          >
            + افزودن بازیکن
          </button>
        </div>

        {teamRows.length === 0 && (
          <p className="text-[11px] text-slate-400">آماری برای این تیم ثبت نشده است.</p>
        )}

        {teamRows.map((row) => (
          <div key={row.key} className="grid grid-cols-12 items-center gap-1.5 rounded-xl bg-slate-50 p-2">
            <select
              className="col-span-4 rounded-lg border border-slate-200 p-1.5 text-xs outline-none focus:border-emerald-500"
              value={row.playerId}
              onChange={(e) => updateRow(row.key, 'playerId', e.target.value)}
            >
              <option value="">انتخاب بازیکن</option>
              {players.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
            </select>

            <div className="col-span-2 flex flex-col items-center">
              <span className="text-[9px] text-slate-400">گل</span>
              <input
                type="number"
                min="0"
                className="w-full rounded-lg border border-slate-200 p-1 text-center text-xs outline-none focus:border-emerald-500"
                value={row.goals}
                onChange={(e) => updateRow(row.key, 'goals', e.target.value)}
              />
            </div>

            <div className="col-span-2 flex flex-col items-center">
              <span className="text-[9px] text-slate-400">پاس گل</span>
              <input
                type="number"
                min="0"
                className="w-full rounded-lg border border-slate-200 p-1 text-center text-xs outline-none focus:border-emerald-500"
                value={row.assists}
                onChange={(e) => updateRow(row.key, 'assists', e.target.value)}
              />
            </div>

            <div className="col-span-1 flex flex-col items-center">
              <span className="text-[9px] text-amber-500">زرد</span>
              <input
                type="number"
                min="0"
                max="2"
                className="w-full rounded-lg border border-slate-200 p-1 text-center text-xs outline-none focus:border-emerald-500"
                value={row.yellowCards}
                onChange={(e) => updateRow(row.key, 'yellowCards', e.target.value)}
              />
            </div>

            <div className="col-span-2 flex flex-col items-center">
              <span className="text-[9px] text-red-500">قرمز</span>
              <input
                type="number"
                min="0"
                max="1"
                className="w-full rounded-lg border border-slate-200 p-1 text-center text-xs outline-none focus:border-emerald-500"
                value={row.redCards}
                onChange={(e) => updateRow(row.key, 'redCards', e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={() => removeRow(row.key)}
              className="col-span-1 grid h-6 w-6 place-items-center rounded-full text-red-400 transition hover:bg-red-50"
              title="حذف سطر"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="ثبت نتیجه مسابقه" size="lg">
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-slate-500">
          <span>{match.homeClub?.name}</span>
          <span>در برابر</span>
          <span>{match.awayClub?.name}</span>
        </div>
        <p className="text-xs text-slate-400">
          تاریخ: {toJalaliFull(match.date)}
        </p>

        {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-3 items-center gap-2">
            <Input
              label={match.homeClub?.name}
              type="number"
              value={homeScore}
              onChange={(e) => setHomeScore(e.target.value)}
              className="text-center"
              min="0"
            />
            <span className="text-center text-2xl font-bold text-slate-300">-</span>
            <Input
              label={match.awayClub?.name}
              type="number"
              value={awayScore}
              onChange={(e) => setAwayScore(e.target.value)}
              className="text-center"
              min="0"
            />
          </div>

          <Input
            label="گزارش (اختیاری)"
            value={report}
            onChange={(e) => setReport(e.target.value)}
          />

          {/* ===== گلزنان و کارت‌ها ===== */}
          <div className="space-y-4 rounded-2xl border border-slate-200 p-3">
            <h4 className="text-xs font-bold text-text-dark">گلزنان و کارت‌ ها (اختیاری)</h4>
            {renderTeamRows('home', match.homeClub?.name, homePlayers)}
            {renderTeamRows('away', match.awayClub?.name, awayPlayers)}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} type="button">انصراف</Button>
            <GradientButton type="submit" disabled={submitting}>
              {submitting ? 'در حال ثبت...' : 'ثبت نتیجه'}
            </GradientButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}